import { useCallback, useEffect, useState } from "react";
import { UserPlus, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ENTITY_MODULE } from "@/lib/notesRouting";

// Assign a note to a teammate (author + managers/admins only). Reuses the
// mention-candidate list (teammates with access to the note's module).
// Uses a Radix Popover so the menu portals to <body> and never clips inside
// scroll containers (e.g. the List view's horizontally-scrolling table).
export function AssignControl({ note, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const moduleKey = note.module || ENTITY_MODULE[note.entity_type];

  const loadCands = useCallback(async () => {
    if (!moduleKey) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notes/mention-candidates", { params: { module: moduleKey } });
      setCands(data.candidates || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [moduleKey]);

  useEffect(() => { if (open) loadCands(); }, [open, loadCands]);

  const assign = async (assigneeId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${note.note_id}/assign`, { assignee_id: assigneeId });
      onUpdated?.(data);
      setOpen(false);
      toast.success(assigneeId ? "Note assigned" : "Assignment cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not assign note");
    } finally { setBusy(false); }
  };

  if (!note.can_assign && !note.assignee_id) return null;

  // Read-only chip (assignee set but current user can't reassign).
  if (!note.can_assign && note.assignee_id) {
    return (
      <span data-testid={`note-assignee-${note.note_id}`}
        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/25">
        <UserPlus className="h-2.5 w-2.5" /><span className="max-w-[110px] truncate">{note.assignee_name}</span>
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {note.assignee_id ? (
          <button type="button" data-testid={`note-assignee-${note.note_id}`} title="Reassign"
            className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/25 hover:border-sky-400/60 transition-colors">
            <UserPlus className="h-2.5 w-2.5" /><span className="max-w-[110px] truncate">{note.assignee_name}</span>
          </button>
        ) : (
          <button type="button" data-testid={`note-assign-${note.note_id}`} title="Assign to a teammate"
            className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-400 hover:text-sky-300 hover:border-sky-500/40 transition-colors">
            <UserPlus className="h-2.5 w-2.5" /> Assign
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" data-testid={`note-assign-menu-${note.note_id}`}
        className="w-52 max-h-60 overflow-y-auto bg-zinc-800 border-zinc-700 text-zinc-100 p-1">
        {note.assignee_id && (
          <button type="button" onClick={() => assign(null)} disabled={busy}
            data-testid={`note-unassign-${note.note_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-zinc-400 hover:bg-zinc-700 rounded-md transition-colors">
            <X className="h-3 w-3" /> Clear assignment</button>
        )}
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-indigo-400" /></div>
        ) : cands.length === 0 ? (
          <p className="text-[11px] text-zinc-500 px-3 py-3">No teammates available.</p>
        ) : cands.map((c) => (
          <button key={c.user_id} type="button" onClick={() => assign(c.user_id)} disabled={busy}
            data-testid={`note-assign-option-${c.user_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-700 rounded-md transition-colors">
            {note.assignee_id === c.user_id ? <Check className="h-3 w-3 text-sky-400" /> : <UserPlus className="h-3 w-3 text-zinc-500" />}
            <span className="truncate">{c.name}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
